/** Explain-mode guidance state (demo-v2 W3) — /api/guidance.
 *
 * A tiny module store, same spirit as the flag probes: one GET per
 * session, mutations update the cache in place. `seen` deliberately does
 * NOT notify — a band must not pop its fade-ask mid-view; the ask shows
 * on the NEXT encounter. Dismiss / mode / reset notify immediately.
 */

import { useEffect, useState } from 'react'
import { apiFetch } from '../../lib/api'

export interface GuidanceTypeState {
  seen_count: number
  dismissed: boolean
}

export interface GuidanceState {
  explain_mode: boolean
  types: Record<string, GuidanceTypeState>
}

let state: GuidanceState | null = null
let statePromise: Promise<GuidanceState> | null = null
const listeners = new Set<() => void>()

function notify() {
  listeners.forEach((fn) => fn())
}

function load(): Promise<GuidanceState> {
  if (!statePromise) {
    statePromise = apiFetch<GuidanceState>('/api/guidance')
      .then((s) => {
        state = s
        notify()
        return s
      })
      .catch(() => {
        // Auth/network hiccup: behave as "mode off" for the session rather
        // than erroring every card; the next full load retries.
        statePromise = null
        const fallback = { explain_mode: false, types: {} }
        state = fallback
        notify()
        return fallback
      })
  }
  return statePromise
}

/** Reactive read; kicks the initial load. */
export function useGuidanceState(): GuidanceState | null {
  const [, bump] = useState(0)
  useEffect(() => {
    const fn = () => bump((n) => n + 1)
    listeners.add(fn)
    void load()
    return () => {
      listeners.delete(fn)
    }
  }, [])
  return state
}

function typeState(key: string): GuidanceTypeState {
  return state?.types[key] ?? { seen_count: 0, dismissed: false }
}

const seenThisSession = new Set<string>()

/** One encounter per key per page-session; silent cache update, no notify. */
export function markSeen(key: string): void {
  if (seenThisSession.has(key)) return
  seenThisSession.add(key)
  apiFetch<GuidanceTypeState>('/api/guidance/seen', {
    method: 'POST',
    body: JSON.stringify({ key }),
  })
    .then((t) => {
      if (state) state.types[key] = t
    })
    .catch(() => {})
}

export function dismissType(key: string): void {
  if (state) state.types[key] = { ...typeState(key), dismissed: true }
  notify()
  apiFetch<GuidanceTypeState>('/api/guidance/dismiss', {
    method: 'POST',
    body: JSON.stringify({ key }),
  }).catch(() => {})
}

/** "keep" on the fade ask: no server write; stop asking this session. */
const keptThisSession = new Set<string>()
export function keepType(key: string): void {
  keptThisSession.add(key)
  notify()
}
export function isKept(key: string): boolean {
  return keptThisSession.has(key)
}

export function setExplainMode(on: boolean): void {
  if (state) state.explain_mode = on
  notify()
  apiFetch<GuidanceState>('/api/guidance/mode', {
    method: 'PUT',
    body: JSON.stringify({ on }),
  })
    .then((s) => {
      state = s
      notify()
    })
    .catch(() => {})
}

export function resetGuidance(): void {
  apiFetch('/api/guidance/reset', { method: 'POST' })
    .then(() => {
      if (state) {
        for (const k of Object.keys(state.types)) {
          state.types[k] = { seen_count: 0, dismissed: false }
        }
      }
      seenThisSession.clear()
      notify()
    })
    .catch(() => {})
}
